import { BAOCustomElement } from '@bao/custom-element'
import { MediaBreakpoints } from '@bao/media'

class ParallaxReveal extends BAOCustomElement() {
  connectedCallback () {
    super.connectedCallback()

    this.setup()
    this.update()
  }

  setupListeners () {
    super.setupListeners()

    this.update = this.update.bind(this)

    window.addEventListener('resize', this.update, { passive: true })
  }

  setup () {
    this.resizeObserver = new ResizeObserver(this.update)

    const images = this.els.image?.elements || [this.els.image?.element]

    images.forEach((image) => {
      if (image) this.resizeObserver.observe(image)
    })

    if (this.els.content?.element) {
      this.resizeObserver.observe(this.els.content.element)
    }

    if (this.els.contentContainer?.element) {
      this.resizeObserver.observe(this.els.contentContainer.element)
    }
  }

  disconnectedCallback () {
    super.disconnectedCallback()

    this.resizeObserver?.disconnect()

    window.removeEventListener('resize', this.update)
  }

  update () {
    if (MediaBreakpoints.isXsViewport) return

    if (!this.els.body?.element || !this.els.content?.element) return

    const images = this.els.image?.elements || [this.els.image?.element]
    const activeImage = images.find((image) => image?.offsetParent !== null)

    if (!activeImage) return

    const viewportHeight = window.innerHeight
    const imageHeight = activeImage.getBoundingClientRect().height
    const contentHeight =
      this.els.content.element.getBoundingClientRect().height

    if (!viewportHeight || !imageHeight || !contentHeight) return

    const imageRevealFactor = 0.12
    const minImageReveal = viewportHeight * 0.06
    const maxImageReveal = imageHeight

    const minContentTopOffset = 32

    const contentGapFactor = 0.04
    const minContentGap = 24
    const maxContentGap = 56

    const contentHiddenStartFactor = 0.24
    const minHiddenStartDepth = viewportHeight * 0.16
    const maxHiddenStartDepth = viewportHeight * 0.36

    const minContentRevealDistance = viewportHeight * 0.25

    const endScrollFactor = 1.4
    const minEndScrollDistance = viewportHeight * 0.08
    const maxEndScrollDistance = viewportHeight * 0.18

    const contentOffsetAdjustment = this.getCSSPixelValue(
      this.els.contentContainer?.element,
      '--Parallax_ContentOffsetAdjustment',
      0,
    )

    const contentGap = this.clamp(
      viewportHeight * contentGapFactor,
      minContentGap,
      maxContentGap,
    )

    const baseRevealAmount = imageHeight * imageRevealFactor

    const contentBottom =
      minContentTopOffset +
      contentOffsetAdjustment +
      contentHeight +
      contentGap

    const minimumRevealAmountToClearContent =
      imageHeight + contentBottom - viewportHeight

    const revealAmount = this.clamp(
      Math.max(baseRevealAmount, minimumRevealAmountToClearContent),
      minImageReveal,
      maxImageReveal,
    )

    const visibleImageHeight = imageHeight - revealAmount
    const imageTop = viewportHeight - visibleImageHeight

    const idealContentTopOffset =
      imageTop - contentOffsetAdjustment - contentHeight - contentGap

    const contentTopOffset = Math.max(
      minContentTopOffset,
      idealContentTopOffset,
    )

    const hiddenStartDepth = this.clamp(
      visibleImageHeight * contentHiddenStartFactor,
      minHiddenStartDepth,
      maxHiddenStartDepth,
    )

    const contentStartOffset = imageTop + hiddenStartDepth

    const contentRevealDistance = Math.max(
      contentStartOffset - contentTopOffset,
      minContentRevealDistance,
    )

    const endScrollDistance = this.clamp(
      revealAmount * endScrollFactor,
      minEndScrollDistance,
      maxEndScrollDistance,
    )

    const bodyHeight =
      viewportHeight + contentRevealDistance + revealAmount + endScrollDistance

    this.els.body.element.style.setProperty(
      '--Parallax_RevealAmount',
      `${revealAmount}px`,
    )
    this.els.body.element.style.setProperty(
      '--Parallax_ContentTopOffset',
      `${contentTopOffset}px`,
    )
    this.els.body.element.style.setProperty(
      '--Parallax_ContentRevealDistance',
      `${contentRevealDistance}px`,
    )
    this.els.body.element.style.setProperty(
      '--Parallax_EndScrollDistance',
      `${endScrollDistance}px`,
    )
    this.els.body.element.style.setProperty(
      '--Parallax_BodyHeight',
      `${bodyHeight}px`,
    )

    this.els.body.element.style.setProperty(
      '--Parallax_ImageHeight',
      `${imageHeight}px`,
    )
    this.els.body.element.style.setProperty(
      '--Parallax_ContentHeight',
      `${contentHeight}px`,
    )
    this.els.body.element.style.setProperty(
      '--Parallax_ImageTop',
      `${imageTop}px`,
    )
  }

  getCSSPixelValue (element, name, fallback = 0) {
    if (!element) return fallback

    const value = window
      .getComputedStyle(element)
      .getPropertyValue(name)
      .trim()

    const parsedValue = parseFloat(value)

    return Number.isNaN(parsedValue) ? fallback : parsedValue
  }

  clamp (value, min, max) {
    return Math.min(Math.max(value, min), max)
  }
}

if (!customElements.get('parallax-reveal')) {
  customElements.define('parallax-reveal', ParallaxReveal)
}
